import { useMemo, useRef, useState } from 'react';
import { useT } from '@/store';
import { Modal } from '@/components/ui/Overlay';
import { ConfirmButton } from '@/components/ui/ConfirmButton';
import { ErrorState } from '@/components/ui/Panel';
import {
  useCommitImport, useDeleteImportTemplate, usePreviewImport, useRevertImport,
} from '@/hooks/useImport';

const ACCEPTED = '.csv,.xlsx,.xlsm,.pdf,.txt,.tsv';

/**
 * Bulk import, in three phases: drop a file, check the mapping, commit.
 *
 * The middle phase is the one that earns its keep. An import is a
 * destructive operation — matching rows are overwritten — so the operator
 * has to be shown what it will do, in their own terms, BEFORE it happens:
 * how many records are new, how many existing ones will be changed, and
 * which rows will not import at all and why. Every one of those numbers
 * comes from the server having validated the whole file, not the five rows
 * on screen.
 *
 * The fourth thing on screen, after a commit, is the undo button. It is
 * shown inline rather than filed away in a history panel because the moment
 * an operator realises they imported the wrong file is the ten seconds
 * immediately after the counts appear.
 */
export function SmartImportModal({ open, onClose, entityType, title }) {
  const t = useT();
  const [file, setFile] = useState(null);
  // null until the dispatcher changes something, and then it wins. Deriving
  // the working mapping from the server's guess rather than copying it into
  // state in the mutation's onSuccess means there is no path where the
  // preview has arrived but the mapping is still empty — which is what a
  // cache restore, a retry, or a StrictMode double-invoke would produce.
  const [mappingOverride, setMappingOverride] = useState(null);
  // One value for a whole file, for a field no column carries (P1-29). The
  // case: a supplier's PDF price list names its quarry in the heading and
  // its table is material + price. Kept here rather than derived from the
  // preview response so the operator's typing is not thrown away each time
  // the preview is recomputed.
  const [constants, setConstants] = useState({});
  const [saveTemplate, setSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const preview = usePreviewImport(entityType);
  const commit = useCommitImport(entityType);
  const revert = useRevertImport(entityType);

  const previewData = preview.data;
  const result = commit.data;
  const phase = result ? 'done' : previewData ? 'map' : 'upload';
  const mapping = useMemo(
    () => mappingOverride ?? previewData?.column_mapping ?? {},
    [mappingOverride, previewData],
  );

  const reset = () => {
    setFile(null);
    setMappingOverride(null);
    setConstants({});
    setSaveTemplate(false);
    setTemplateName('');
    preview.reset();
    commit.reset();
    revert.reset();
  };

  const close = () => {
    reset();
    onClose();
  };

  const removeTemplate = useDeleteImportTemplate(entityType);

  const accept = (chosen) => {
    if (!chosen) return;
    setFile(chosen);
    commit.reset();
    revert.reset();
    setMappingOverride(null);
    preview.mutate({ file: chosen });
  };

  // V-65. Re-read the SAME file under a different saved template.
  //
  // The preview auto-applies templates[0] — alphabetically first by name —
  // whenever no template is named, and the reply says which one it used.
  // Until this existed the operator could read that sentence and do nothing
  // about it. Measured: a carrier with templates for two fuel suppliers
  // uploads the Neste statement, gets Circle K's mapping applied because C
  // sorts before N, and the match field (license_plate) ends up mapped to
  // nothing at all — every row fails on a required field.
  //
  // Re-previewed rather than remapped locally, because a template is a set
  // of HEADER names and only the server knows which of them this file
  // actually has; applying it here would mean a second copy of that rule.
  const applyTemplate = (templateId) => {
    if (!file) return;
    setMappingOverride(null);
    setConstants({});
    preview.mutate({ file, templateId: templateId || undefined });
  };

  // A target field claimed by one column must not still be offered to
  // another: two columns mapped to contact_phone means the second silently
  // overwrites the first, which is the same trap guess_mapping() avoids on
  // the server.
  const claimed = useMemo(() => new Set(Object.values(mapping).filter(Boolean)), [mapping]);

  // A required field is satisfied by a column OR by one value for the whole
  // file. Counting only columns would leave the commit button disabled on a
  // file the operator has already told us how to read.
  const missingRequired = useMemo(
    () => (previewData?.target_fields ?? [])
      .filter((f) => f.required && !claimed.has(f.name) && !constants[f.name]?.trim())
      .map((f) => f.name),
    [previewData, claimed, constants],
  );

  return (
    <Modal
      open={open}
      onClose={close}
      title={title ?? t('import.title')}
      width="max-w-[860px]"
      footer={
        <>
          <button type="button" className="btn" onClick={close}>
            {phase === 'done' ? t('common.closeBtn') : t('common.cancel')}
          </button>
          {phase === 'map' ? (
            <button
              type="button"
              className="btn btn-primary"
              disabled={commit.isPending || missingRequired.length > 0}
              onClick={() =>
                commit.mutate({
                  file, mapping, constants, saveAsTemplate: saveTemplate, templateName,
                })
              }
            >
              {commit.isPending ? t('import.committing') : t('import.commitBtn')}
            </button>
          ) : null}
          {phase === 'done' ? (
            <button type="button" className="btn btn-primary" onClick={reset}>
              {t('import.importAnother')}
            </button>
          ) : null}
        </>
      }
    >
      {phase === 'upload' ? (
        <DropZone
          dragging={dragging}
          setDragging={setDragging}
          inputRef={inputRef}
          onFile={accept}
          pending={preview.isPending}
        />
      ) : null}

      {preview.isError ? <ErrorState error={preview.error} /> : null}

      {phase === 'map' ? (
        <MapPhase
          data={previewData}
          mapping={mapping}
          setMapping={setMappingOverride}
          claimed={claimed}
          missingRequired={missingRequired}
          constants={constants}
          setConstants={setConstants}
          onConstantsSettled={(next) => preview.mutate({ file, constants: next })}
          saveTemplate={saveTemplate}
          setSaveTemplate={setSaveTemplate}
          templateName={templateName}
          setTemplateName={setTemplateName}
          onChooseAnother={reset}
          onApplyTemplate={applyTemplate}
          onDeleteTemplate={(templateId) =>
            // Re-previewed after the delete, because the server picks
            // templates[0] when none is named — removing the one that was
            // applied changes which mapping this very file gets, and a
            // screen still showing the old one would be showing a mapping
            // that no longer exists.
            removeTemplate.mutateAsync(templateId).then(() => applyTemplate(''))}
          busy={preview.isPending || removeTemplate.isPending}
        />
      ) : null}

      {commit.isError ? <ErrorState error={commit.error} /> : null}

      {phase === 'done' ? (
        <DonePhase result={result} revert={revert} />
      ) : null}
    </Modal>
  );
}

function DropZone({ dragging, setDragging, inputRef, onFile, pending }) {
  const t = useT();
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        onFile(e.dataTransfer.files?.[0]);
      }}
      className={`rounded-[12px] border-2 border-dashed p-8 text-center transition-colors ${
        dragging ? 'border-green bg-green/8' : 'border-line'
      }`}
    >
      <div className="mb-2 text-[28px]" aria-hidden="true">
        ⇪
      </div>
      <p className="mb-1 flex items-center justify-center gap-1.5 text-[13px] font-semibold">
        {t('import.dropHeading')}
        {/* A hover-only explainer, native title tooltip — same pattern as
            client/ClientPortal.jsx's weight-estimate hint — for the one
            question this screen otherwise leaves unanswered: what actually
            happens after a file lands here. */}
        <span
          className="inline-flex h-4 w-4 shrink-0 cursor-help items-center justify-center rounded-full border border-line text-[10px] font-normal text-dim"
          title={t('import.howItWorksTooltip')}
          aria-label={t('import.howItWorksTooltip')}
        >
          ?
        </span>
      </p>
      <p className="mb-4 text-[11px] text-dim">{t('import.dropFormats')}</p>
      <button
        type="button"
        className="btn btn-primary"
        disabled={pending}
        onClick={() => inputRef.current?.click()}
      >
        {pending ? t('import.reading') : t('import.chooseFile')}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED}
        className="hidden"
        onChange={(e) => onFile(e.target.files?.[0])}
      />
    </div>
  );
}

function MapPhase({
  data, mapping, setMapping, claimed, missingRequired,
  constants, setConstants, onConstantsSettled,
  saveTemplate, setSaveTemplate, templateName, setTemplateName, onChooseAnother,
  onApplyTemplate, onDeleteTemplate, busy,
}) {
  const t = useT();
  const { summary, headers, target_fields: targetFields, preview_rows: rows, failures } = data;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-[12px] font-semibold">{data.filename}</div>
          {data.applied_template ? (
            <div className="text-[11px] text-green">
              {t('import.templateApplied', { name: data.applied_template.template_name })}
            </div>
          ) : null}
        </div>
        <button type="button" className="btn !px-2.5 !py-1 !text-[11px]" onClick={onChooseAnother}>
          {t('import.chooseAnother')}
        </button>
      </div>

      <TemplatePicker
        templates={data.templates ?? []}
        appliedId={data.applied_template?.template_id ?? ''}
        onApply={onApplyTemplate}
        onDelete={onDeleteTemplate}
        busy={busy}
      />

      {/* What committing will actually do, before it does it. will_skip is
          only ever nonzero for an insert-only entity re-importing rows it
          already has (vehicle_gps_ping) — the tile stays out of the way
          for every other import, where it's always 0. */}
      <div className={`grid gap-2 ${summary.will_skip > 0 ? 'grid-cols-4' : 'grid-cols-3'}`}>
        <Stat label={t('import.willInsert')} value={summary.will_insert} tone="text-green" />
        <Stat label={t('import.willUpdate')} value={summary.will_update} tone="text-amber" />
        {summary.will_skip > 0 ? (
          <Stat label={t('import.willSkip')} value={summary.will_skip} tone="text-dim" />
        ) : null}
        <Stat label={t('import.willFail')} value={summary.will_fail} tone="text-red" />
      </div>

      {summary.will_update > 0 ? (
        <p className="rounded-[10px] border border-amber/25 bg-amber/8 px-3 py-2 text-[11px] text-amber">
          {t('import.overwriteWarning', { n: summary.will_update })}
        </p>
      ) : null}

      {/* V-114. Rows naming a record this tenant archived. They used to be
          counted as "will be updated" — and the commit then wrote into the
          archived row and called it an update, so the preview and the commit
          agreed on the same wrong answer. Said here, before committing,
          because this is the one moment the operator can act on it: if the
          record should come back, it is restored from its own screen, and
          the file is imported after. */}
      {summary.will_skip_archived > 0 ? (
        <p className="rounded-[10px] border border-line bg-white/4 px-3 py-2 text-[11px] text-dim">
          {t('import.archivedSkipWarning', { n: summary.will_skip_archived })}
        </p>
      ) : null}

      <div>
        <h4 className="mb-2 text-[10.5px] font-bold uppercase tracking-[0.03em] text-dim">
          {t('import.mappingHeading')}
        </h4>
        <div className="grid gap-2 sm:grid-cols-2">
          {headers.map((header) => (
            <label key={header} className="flex items-center gap-2">
              <span className="min-w-0 flex-1 truncate text-[11.5px]" title={header}>
                {header}
              </span>
              <span aria-hidden="true" className="text-dim">→</span>
              <select
                className="field !w-[150px] !py-1 !text-[11px]"
                value={mapping[header] ?? ''}
                onChange={(e) => {
                  const next = { ...mapping };
                  if (e.target.value) next[header] = e.target.value;
                  else delete next[header];
                  setMapping(next);
                }}
              >
                <option value="">{t('import.ignoreColumn')}</option>
                {targetFields.map((field) => (
                  <option
                    key={field.name}
                    value={field.name}
                    // Still selectable if it is THIS column's own choice.
                    disabled={claimed.has(field.name) && mapping[header] !== field.name}
                  >
                    {t(`import.field.${field.name}`)}
                    {field.required ? ' *' : ''}
                  </option>
                ))}
              </select>
            </label>
          ))}
        </div>
        {missingRequired.length > 0 ? (
          <p className="mt-2 text-[11px] text-red">
            {t('import.missingRequired', {
              fields: missingRequired.map((f) => t(`import.field.${f}`)).join(', '),
            })}
          </p>
        ) : null}
      </div>

      <ConstantFields
        targetFields={targetFields}
        claimed={claimed}
        constants={constants}
        setConstants={setConstants}
        onSettled={onConstantsSettled}
      />

      <PreviewTable rows={rows} mapping={mapping} targetFields={targetFields} />

      {failures.length > 0 ? <FailureList failures={failures} /> : null}

      <label className="flex items-center gap-2 text-[11.5px]">
        <input
          type="checkbox"
          checked={saveTemplate}
          onChange={(e) => setSaveTemplate(e.target.checked)}
        />
        {t('import.saveTemplate')}
      </label>
      {saveTemplate ? (
        <input
          className="field !text-[11.5px]"
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          placeholder={t('import.templateNamePh')}
        />
      ) : null}
    </div>
  );
}

/**
 * Which saved template this file is being read with — and the two things a
 * carrier can do about it.
 *
 * V-65. The preview auto-applies templates[0], alphabetically first by
 * name, whenever the caller does not name one. The reply has always said
 * WHICH template it used, and the screen has always shown that sentence —
 * and there was nothing to do about it, because the list of the others (ids
 * included, in the same reply) was rendered nowhere and the delete route
 * had no caller at all.
 *
 * Measured on a live server: a carrier with saved templates for two fuel
 * suppliers uploads the Neste statement, Circle K's mapping is applied
 * because C sorts before N, and license_plate — the match field — ends up
 * mapped to nothing. Every row fails on a required field, and the only way
 * out was to remap every column by hand: exactly the work the template was
 * saved to avoid.
 *
 * "Guess from the file" is an option and not a blank one. It is what the
 * import does for a carrier with no templates at all, and it is the right
 * answer for a layout none of the saved ones match — without it, a fleet
 * with three templates can never get back to the plain behaviour.
 *
 * Deleting is behind a confirm and says what it does NOT touch: the rows
 * already imported under that template stay exactly as they are. A template
 * is a reading of a file, not a record of anything.
 */
function TemplatePicker({ templates, appliedId, onApply, onDelete, busy }) {
  const t = useT();
  if (!templates.length) return null;

  const applied = templates.find((tpl) => tpl.template_id === appliedId);

  return (
    <div className="flex flex-wrap items-end gap-2 rounded-[11px] border border-line bg-white/2 p-2.5">
      <label className="block">
        <span className="mb-1 block text-[10px] font-bold uppercase tracking-[0.03em] text-dim">
          {t('import.templateLabel')}
        </span>
        <select
          className="field !w-auto !py-1 !text-[11px]"
          value={appliedId}
          disabled={busy}
          onChange={(e) => onApply(e.target.value)}
        >
          <option value="">{t('import.templateNone')}</option>
          {templates.map((tpl) => (
            <option key={tpl.template_id} value={tpl.template_id}>{tpl.template_name}</option>
          ))}
        </select>
      </label>
      {applied ? (
        <ConfirmButton
          label={t('import.templateDeleteBtn')}
          confirmLabel={t('common.confirmDelete')}
          hint={t('import.templateDeleteHint')}
          disabled={busy}
          onConfirm={() => onDelete(applied.template_id)}
        />
      ) : null}
    </div>
  );
}

/**
 * One value for a whole file, for a field no column carries (P1-29).
 *
 * The file this exists for is the one requirements.txt names as
 * pdfplumber's reason for being here: a supplier's PDF price list with
 * "UAB X, Kruonio karjeras, kainos nuo 2026-01-01" in the heading and a
 * two-column table underneath. There is no quarry column to map, so before
 * this every row of it failed with "quarry not found" — the exact file the
 * feature was built for was the one it could not read.
 *
 * Only offered for fields nothing is mapped to. A column always wins; this
 * is for the gap, not for overriding what the file already says.
 *
 * The re-preview happens on blur rather than per keystroke: the verdict
 * above ("40 new, 12 will fail") is computed on the server from the whole
 * file, and recomputing it on every character would upload the spreadsheet
 * once per letter typed.
 */
function ConstantFields({ targetFields, claimed, constants, setConstants, onSettled }) {
  const t = useT();
  const available = targetFields.filter((f) => !claimed.has(f.name));
  if (available.length === 0) return null;

  return (
    <div>
      <h4 className="mb-1 text-[10.5px] font-bold uppercase tracking-[0.03em] text-dim">
        {t('import.constantsHeading')}
      </h4>
      <p className="mb-2 text-[11px] leading-snug text-dim">{t('import.constantsHint')}</p>
      <div className="grid gap-2 sm:grid-cols-2">
        {available.map((field) => (
          <label key={field.name} className="flex items-center gap-2">
            <span className="min-w-0 flex-1 truncate text-[11.5px]">
              {t(`import.field.${field.name}`)}
              {field.required ? ' *' : ''}
            </span>
            <input
              className="field !w-[150px] !py-1 !text-[11px]"
              value={constants[field.name] ?? ''}
              placeholder={t('import.constantPh')}
              onChange={(e) => setConstants({ ...constants, [field.name]: e.target.value })}
              onBlur={() => onSettled?.(constants)}
            />
          </label>
        ))}
      </div>
    </div>
  );
}

/** The five rows, shown as the values that would actually be STORED —
 *  normalised phone, trimmed name, parsed date — not as the raw cells. The
 *  point of a preview here is to confirm the sanitisation did the right
 *  thing, and raw text would confirm nothing. */
function PreviewTable({ rows, mapping, targetFields }) {
  const t = useT();
  const columns = targetFields.filter((f) => Object.values(mapping).includes(f.name));
  if (columns.length === 0) return null;

  return (
    <div>
      <h4 className="mb-2 text-[10.5px] font-bold uppercase tracking-[0.03em] text-dim">
        {t('import.previewHeading')}
      </h4>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-[11px]">
          <thead>
            <tr className="text-dim">
              <th className="px-2 py-1 font-semibold">#</th>
              {columns.map((field) => (
                <th key={field.name} className="px-2 py-1 font-semibold">
                  {t(`import.field.${field.name}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.row_number} className="border-t border-line">
                <td className="px-2 py-1 text-dim">{row.row_number}</td>
                {columns.map((field) => (
                  <td key={field.name} className="px-2 py-1">
                    {row.errors[field.name] ? (
                      <span className="text-red">{t(row.errors[field.name])}</span>
                    ) : (
                      <span className="text-ink">{formatCell(row.values[field.name])}</span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function formatCell(value) {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
}

/** Every failing row in the file, by the row number Excel shows in its
 *  margin — not just the ones inside the preview window. */
function FailureList({ failures }) {
  const t = useT();
  return (
    <details className="rounded-[10px] border border-red/25 bg-red/8 px-3 py-2">
      <summary className="cursor-pointer text-[11.5px] font-semibold text-red-ink">
        {t('import.failuresHeading', { n: failures.length })}
      </summary>
      <ul className="mt-2 space-y-1 text-[11px]">
        {failures.map((row) => (
          <li key={row.row_number}>
            <span className="text-dim">{t('import.rowN', { n: row.row_number })}:</span>{' '}
            {Object.entries(row.errors).map(([field, key]) => (
              <span key={field} className="mr-2">
                {t(`import.field.${field}`)} — {t(key, { row: row.duplicate_of_row })}
              </span>
            ))}
          </li>
        ))}
      </ul>
    </details>
  );
}

function DonePhase({ result, revert }) {
  const t = useT();
  const reverted = revert.data;

  return (
    <div className="space-y-3">
      <div
        role="status"
        className="rounded-[11px] border border-green/25 bg-green/8 px-4 py-3 text-[13px] font-semibold text-green"
      >
        {t('import.resultBadge', { inserted: result.inserted, updated: result.updated })}
        {result.skipped_archived > 0 ? (
          <span className="ml-2 font-normal text-dim">
            {t('import.resultSkippedArchived', { n: result.skipped_archived })}
          </span>
        ) : null}
        {result.skipped > 0 ? (
          <span className="ml-2 text-dim">{t('import.resultSkipped', { n: result.skipped })}</span>
        ) : null}
        {result.failed > 0 ? (
          <span className="ml-2 text-red-ink">{t('import.resultFailed', { n: result.failed })}</span>
        ) : null}
      </div>

      {result.failures.length > 0 ? <FailureList failures={result.failures} /> : null}

      {result.credentials?.length ? <CredentialList credentials={result.credentials} /> : null}

      {reverted ? (
        <p className="text-[11.5px] text-dim">
          {t('import.reverted', { deleted: reverted.deleted, restored: reverted.restored })}
        </p>
      ) : (
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn !text-[11.5px]"
            disabled={revert.isPending}
            onClick={() => revert.mutate(result.batch_id)}
          >
            {revert.isPending ? t('import.reverting') : t('import.revertBtn')}
          </button>
          <span className="text-[11px] text-dim">{t('import.revertHint')}</span>
        </div>
      )}

      {revert.isError ? <ErrorState error={revert.error} /> : null}
    </div>
  );
}

/**
 * The logins an import just created for drivers — the one and only time
 * they are readable.
 *
 * A driver imported without credentials exists everywhere except the one
 * screen they need: the Driver App's sign-in form. So the import issues
 * them, exactly as creating one driver by hand does — and, exactly as
 * there, the plaintext password is never stored. It is hashed on the way
 * into the database and no route can produce it again. If this panel is
 * closed without the list being copied, the passwords are gone and each
 * driver needs a reset.
 *
 * Which is why the warning is not a footnote and the copy button copies
 * everything at once: fifteen rows retyped by hand is fifteen chances to
 * transpose a character in a credential nobody can check afterwards.
 */
function CredentialList({ credentials }) {
  const t = useT();
  const [copied, setCopied] = useState(false);

  const asText = credentials
    .map((c) => `${c.full_name}\t${c.username}\t${c.password}`)
    .join('\n');

  return (
    <div className="rounded-[11px] border border-amber/30 bg-amber/8 p-3">
      <h4 className="panel-title !mb-1 text-amber">{t('import.credentialsTitle')}</h4>
      <p className="mb-2 text-[11.5px] leading-snug text-dim">
        {t('import.credentialsNote', { n: credentials.length })}
      </p>
      <div className="max-h-56 overflow-auto">
        <table className="w-full text-left text-[11.5px]">
          <thead className="text-dim">
            <tr>
              <th className="py-1 pr-3 font-semibold">{t('import.field.full_name')}</th>
              <th className="py-1 pr-3 font-semibold">{t('import.colUsername')}</th>
              <th className="py-1 font-semibold">{t('import.colPassword')}</th>
            </tr>
          </thead>
          <tbody>
            {credentials.map((c) => (
              <tr key={c.driver_id} className="border-t border-line">
                <td className="py-1 pr-3">{c.full_name}</td>
                <td className="py-1 pr-3 font-mono">{c.username}</td>
                <td className="py-1 font-mono">{c.password}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button
        type="button"
        className="btn mt-2 !text-[11.5px]"
        onClick={() => {
          // Best effort: clipboard access is denied outright in some
          // browser configurations, and a copy button that throws into the
          // console while the panel says nothing is worse than one that
          // simply does not confirm. The table above stays readable either
          // way, which is the actual fallback.
          navigator.clipboard?.writeText(asText).then(
            () => setCopied(true),
            () => setCopied(false),
          );
        }}
      >
        {copied ? t('import.credentialsCopied') : t('import.copyCredentials')}
      </button>
    </div>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className="rounded-[10px] border border-line bg-white/2 px-3 py-2 text-center">
      <div className={`text-[19px] font-bold ${tone}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-[0.03em] text-dim">{label}</div>
    </div>
  );
}
